import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  BookOpen,
  Calendar,
  Settings,
  ChevronLeft,
  ChevronRight,
  School,
  ClipboardList,
  HelpCircle,
  UserPlus,
  Clock,
  CheckCircle2,
  GraduationCap as Exam,
  Star,
  UserCog,
  Wallet,
  UsersRound,
  Trophy,
  Presentation
} from 'lucide-react';



const SideBar = ({ isOpen, toggleSidebar, onCollapseChange }) => {
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const sidebarRef = useRef(null);

  // Notifier le Layout des changements d'état de collapse
  useEffect(() => {
    if (onCollapseChange) {
      onCollapseChange(isCollapsed);
    }
  }, [isCollapsed, onCollapseChange]);

  // Inject custom scrollbar styles
  useEffect(() => {
    const styleId = 'sidebar-scrollbar-styles';
    let existingStyle = document.getElementById(styleId);
    
    if (!existingStyle) {
      const style = document.createElement('style');
      style.id = styleId;
      document.head.appendChild(style);
      existingStyle = style;
    }

    existingStyle.innerHTML = `
      .sidebar-nav {
        scrollbar-width: ${isHovered ? 'thin' : 'none'};
        scrollbar-color: ${isHovered ? 'rgba(255, 255, 255, 0.2) transparent' : 'transparent'};
      }
      
      .sidebar-nav::-webkit-scrollbar {
        width: ${isHovered ? '6px' : '0px'};
        background: transparent;
      }
      
      .sidebar-nav::-webkit-scrollbar-track {
        background: transparent;
      }
      
      .sidebar-nav::-webkit-scrollbar-thumb {
        background: ${isHovered ? 'rgba(255, 255, 255, 0.2)' : 'transparent'};
        border-radius: 3px;
      }
      
      .sidebar-nav::-webkit-scrollbar-thumb:hover {
        background: ${isHovered ? 'rgba(255, 255, 255, 0.3)' : 'transparent'};
      }
    `;
  }, [isHovered]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        if (window.innerWidth < 1024 && isOpen) {
          toggleSidebar();
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, toggleSidebar]);

  const handleLinkClick = () => {
    // Fermer la sidebar sur mobile
    if (window.innerWidth < 1024) {
      toggleSidebar();
    }
    
    // Scroll to top après navigation
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 100);
  };

  const navigation = [
    {
      name: 'Tableau de bord',
      href: '/dashboard',
      icon: LayoutDashboard,
      category: 'main'
    },
    {
      name: 'Inscription',
      href: '/inscriptions',
      icon: UserPlus,
      category: 'gestion'
    },
    {
      name: 'Étudiants',
      href: '/etudiants',
      icon: Users,
      category: 'gestion'
    },
    {
      name: 'Parents',
      href: '/parents',
      icon: Users,
      category: 'gestion'
    },
    {
      name: 'Enseignants',
      href: '/enseignants',
      icon: UsersRound,
      category: 'gestion'
    },

    {
      name: 'Emploi du temps',
      href: '/liste-emplois',
      icon: Calendar,
      category: 'academique'
    },
    {
      name: 'Assiduité',
      href: '/assiduite',
      icon: CheckCircle2,
      category: 'suivi'
    },
    {
      name: 'Ponctualité',
      href: '/ponctualite',
      icon: Clock,
      category: 'suivi'
    },
    {
      name: 'Examens',
      href: '/examens',
      icon: Exam,
      category: 'evaluation'
    },
    {
      name: 'Évaluation',
      href: '/evaluations',
      icon: Star,
      category: 'evaluation'
    },
    {
      name: 'Concours',
      href: '/concours',
      icon: Trophy,
      category: 'evaluation'
    },
    {
      name: 'Soutenance',
      href: '/soutenance',
      icon: Presentation,
      category: 'evaluation'
    },
    {
      name: 'Bulletins & Notes',
      href: '/bulletins-notes',
      icon: ClipboardList,
      category: 'evaluation'
    },
    {
      name: 'Finance',
      href: '/finance',
      icon: Wallet,
      category: 'administration'
    },
    {
      name: 'Utilisateurs',
      href: '/utilisateurs',
      icon: UserCog,
      category: 'administration'
    },

    {
      name: 'Paramètres',
      href: '/settings',
      icon: Settings,
      category: 'system'
    },
    {
      name: 'Aide',
      href: '/help',
      icon: HelpCircle,
      category: 'system'
    },
  ];

  const categoryLabels = {
    main: '',
    gestion: 'Gestion',
    academique: 'Académique',
    suivi: 'Suivi',
    evaluation: 'Évaluation',
    administration: 'Administration',
    system: 'Système'
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const renderNavigationSection = (category) => {
    const items = navigation.filter(item => item.category === category);
    if (items.length === 0) return null;

    return (
      <div key={category} className="mb-2">
        <div className="space-y-0.5">
          {items.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.name}
                to={item.href}
                onClick={handleLinkClick}
                className={`group flex items-center px-4 py-2.5 text-base font-medium rounded-xl transition-all duration-250 ease-out ${isActive(item.href)
                    ? 'bg-white/20 text-white shadow-lg shadow-black/10 scale-[0.98]'
                    : 'text-blue-50 hover:bg-white/15 hover:text-white hover:shadow-md hover:shadow-black/5 hover:scale-[0.99]'
                  } ${isCollapsed ? 'justify-center' : ''} active:scale-95 relative overflow-hidden`}
              >
                {/* Effet de ripple au clic */}
                <div className="absolute inset-0 bg-white/10 transform scale-0 group-active:scale-100 transition-transform duration-150 rounded-xl"></div>
                <Icon className={`${isCollapsed ? 'h-6 w-6' : 'h-5 w-5'} flex-shrink-0 transition-transform duration-200 ${!isCollapsed && !isActive(item.href) ? 'group-hover:scale-110' : ''}`} />
                {!isCollapsed && (
                  <span className="ml-4 font-semibold tracking-wide select-none">
                    {item.name}
                  </span>
                )}
              </Link>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40 lg:hidden" onClick={toggleSidebar} />
      )}

      <aside
        ref={sidebarRef}
        className={`fixed inset-y-0 left-0 z-50 bg-[#202654] transform transition-all duration-300 ease-in-out ${isOpen ? 'translate-x-0' : '-translate-x-full'
          } lg:translate-x-0 ${isCollapsed ? 'w-16' : 'w-64'} shadow-lg flex flex-col`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Logo */}
        <div className="flex items-center justify-between h-20 px-6 border-b border-white/10">
          <div className="flex items-center">
            <div className="flex items-center justify-center w-12 h-12 rounded-2xl bg-gradient-to-br from-white/20 to-white/10 shadow-lg border border-white/20">
              <School className="h-7 w-7 text-white" />
            </div>
            {!isCollapsed && (
              <span className="ml-5 text-xl font-bold tracking-wider text-white select-none">
                B-School
              </span>
            )}
          </div>
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="p-2.5 rounded-xl text-white/70 hover:text-white hover:bg-white/10 transition-all duration-200 lg:block hidden hover:scale-105 active:scale-95"
          >
            {isCollapsed ? <ChevronRight className="h-5 w-5" /> : <ChevronLeft className="h-5 w-5" />}
          </button>
        </div>

        {/* Navigation */}
        <nav className="sidebar-nav flex-1 px-3 py-3 space-y-1 overflow-y-auto">
          {renderNavigationSection('main')}
          {renderNavigationSection('gestion')}
          {renderNavigationSection('academique')}
          {renderNavigationSection('suivi')}
          {renderNavigationSection('evaluation')}
          {renderNavigationSection('administration')}
          {renderNavigationSection('system')}
        </nav>

        {/* Version */}
        {!isCollapsed && (
          <div className="px-4 py-3 border-t border-white/10">
            <div className="px-3 py-1.5 rounded-xl bg-white/10 shadow-inner">
              <p className="text-xs text-white/70 text-center font-semibold tracking-wide select-none">
                Version 1.0.0
              </p>
            </div>
          </div>
        )}
      </aside>
    </>
  );
};

export default SideBar;
